import type { ScanCandidateDto, ScanWindow } from 'shared';

import { compactDollars, formatSignedPct as formatPct } from '../../lib/marketConsoleFormat';
import { cn } from '../../lib/utils';
import { Badge } from '../ui/Badge';

import { WatchStarButton as WatchButton } from './WatchStarButton';

function CoilBadge({ candidate }: { candidate: ScanCandidateDto }) {
  if (!candidate.coil) return <span className="text-xs text-text-subtle">No coil data</span>;
  if (candidate.coil.isTight) {
    return <Badge tone="success">Tight {candidate.coil.setup ?? ''}</Badge>;
  }
  return <span className="text-xs text-text-muted">Not tight</span>;
}

function SymbolCell({ candidate }: { candidate: ScanCandidateDto }) {
  return (
    <div className="min-w-0">
      <div className="font-semibold">
        {candidate.symbol}
        {candidate.pairedEtf ? (
          <span className="font-normal text-text-muted"> / {candidate.pairedEtf.symbol}</span>
        ) : null}
      </div>
      <div className="truncate text-[11px] text-text-muted">
        {candidate.vendorIndustry ? (
          <span title={`Your group; the data vendor lists ${candidate.vendorIndustry}`}>
            {candidate.industry} · your group
          </span>
        ) : (
          candidate.industry
        )}
      </div>
    </div>
  );
}

function ExtensionValue({
  candidate,
  maxAtrExtension,
}: {
  candidate: ScanCandidateDto;
  maxAtrExtension: number | undefined;
}) {
  const extended = maxAtrExtension !== undefined && candidate.atrExtension > maxAtrExtension;
  return (
    <span className={cn(extended && 'text-warn')}>
      {candidate.atrExtension.toFixed(1)}×{extended ? ' ext' : ''}
    </span>
  );
}

/** Scan results as a table on desktop and a card list on mobile. */
export function StockCandidateList({
  candidates,
  horizon,
  emptyText,
  maxAtrExtension,
}: {
  candidates: readonly ScanCandidateDto[];
  horizon: ScanWindow;
  emptyText: string;
  /** Marks names stretched beyond this many ATRs over the 50-day as extended. */
  maxAtrExtension?: number;
}) {
  if (candidates.length === 0) {
    return <p className="px-4 py-6 text-sm text-text-muted">{emptyText}</p>;
  }
  return (
    <>
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full text-sm tabular-nums">
          <thead className="text-xs text-text-muted">
            <tr className="border-b border-border">
              <th scope="col" className="w-12 px-2 py-2">
                <span className="sr-only">Watch</span>
              </th>
              <th scope="col" className="px-3 py-2 text-left font-medium">
                Stock
              </th>
              <th scope="col" className="px-3 py-2 text-right font-medium">
                {horizon}
              </th>
              <th scope="col" className="px-3 py-2 text-right font-medium">
                ADR%
              </th>
              <th scope="col" className="px-3 py-2 text-right font-medium">
                ATRs over 50D
              </th>
              <th scope="col" className="px-3 py-2 text-right font-medium">
                $ volume 30D
              </th>
              <th scope="col" className="px-3 py-2 text-left font-medium">
                Coil
              </th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {candidates.map((candidate) => (
              <tr key={candidate.symbol} className="border-b border-border last:border-0">
                <td className="px-2 py-1.5">
                  <WatchButton candidate={candidate} />
                </td>
                <td className="px-3 py-1.5 font-sans">
                  <SymbolCell candidate={candidate} />
                </td>
                <td
                  className={cn(
                    'px-3 py-1.5 text-right',
                    candidate.performance[horizon] >= 0 ? 'text-success' : 'text-danger',
                  )}
                >
                  {formatPct(candidate.performance[horizon])}
                </td>
                <td className="px-3 py-1.5 text-right">{candidate.adrPct.toFixed(1)}</td>
                <td className="px-3 py-1.5 text-right">
                  <ExtensionValue candidate={candidate} maxAtrExtension={maxAtrExtension} />
                </td>
                <td className="px-3 py-1.5 text-right">
                  {compactDollars.format(candidate.averageDollarVolume30d)}
                </td>
                <td className="px-3 py-1.5 font-sans">
                  <CoilBadge candidate={candidate} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ul className="divide-y divide-border md:hidden">
        {candidates.map((candidate) => (
          <li key={candidate.symbol} className="flex items-start gap-2 px-3 py-3">
            <WatchButton candidate={candidate} />
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <SymbolCell candidate={candidate} />
                <span
                  className={cn(
                    'font-mono text-sm tabular-nums',
                    candidate.performance[horizon] >= 0 ? 'text-success' : 'text-danger',
                  )}
                >
                  {horizon} {formatPct(candidate.performance[horizon])}
                </span>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[11px] tabular-nums text-text-muted">
                <span>ADR {candidate.adrPct.toFixed(1)}%</span>
                <span>
                  <ExtensionValue candidate={candidate} maxAtrExtension={maxAtrExtension} /> ATR
                  over 50D
                </span>
                <span>{compactDollars.format(candidate.averageDollarVolume30d)}/day</span>
                <CoilBadge candidate={candidate} />
              </div>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}
